import React, { useRef, useEffect, useReducer, useState } from "react";
import { useTranslation } from "react-i18next";
import defaultLocale from "mapbox-gl";
import mapboxgl, { LngLatBoundsLike, LngLatLike } from "mapbox-gl";
import MapFilter from "./mapFilter";
import MapPopup from "./popup";
import { MapTouchEvent } from "mapbox-gl";
import { initialMapState, mapReducer } from "./map.reducer";
import { MapActionsTypes } from "./map.actions";
import { MapFilterSize } from "./map.interfaces";
import { useMapData } from "./hooks/useMapData";
import MapMenu from "./mapMenu";
import useMediaQuery from "../../hooks/useMediaQuery";
import { useMapEvents } from "./hooks/useMapEvents";
import { MapTypes } from "./mapConfig";
import ItemNameTooltip from "./mapItemNameTooltip";

export interface IHeroMap {
  show: boolean;
}

const HeroMap = ({ show }: IHeroMap) => {
  const mapRef = useRef<HTMLDivElement | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [locale, setLocale] = useState<string | null>(null);
  const [mapTexts, setMapTexts] = useState<any>(defaultLocale);
  const isMobile = useMediaQuery(576);
  const [hideMapMenu, setMapMenuHidden] = useState(false);
  const [popupClosed, setPopupClosed] = useState(true);

  const { t } = useTranslation();

  const [
    {
      map,
      currentMapId,
      showMap,
      showLoader,
      hideFilter,
      filterModalSize,
      popupPositon,
    },
    dispatch,
  ] = useReducer(mapReducer, initialMapState);

  const {
    cmsMapData,
    currentCmsMapData,
    mapConfig,
    customMapLayers,
    searchData,
    mapLinkedWithPage,
  } = useMapData(map, currentMapId, locale);

  const { selectedLocationData, showMapMenu, hoveredItemName } = useMapEvents({
    map,
    mapContainerRef,
    cmsMapData,
    currentCmsMapData,
    customMapLayers,
    mapConfig,
    hideMapMenu,
    mapLinkedWithPage,
    searchData,
    popupClosed,
  });

  useEffect(() => {
    if (document.documentElement.lang) {
      setLocale(document.documentElement.lang);
    }
  }, []);

  useEffect(() => {
    if (locale) {
      const texts = {
        "FullscreenControl.Enter": t("map.enterFullscreen"),
        "FullscreenControl.Exit": t("map.exitFullscreen"),
        "Map.Title": t("map.title"),
        "NavigationControl.ResetBearing": t("map.resetToNorth"),
        "NavigationControl.ZoomIn": t("map.zoomIn"),
        "NavigationControl.ZoomOut": t("map.zoomOut"),
        "ScrollZoomBlocker.CtrlMessage": t("map.ctrlMessage"),
        "ScrollZoomBlocker.CmdMessage": t("map.cmdMessage"),
        "TouchPanBlocker.Message": t("map.touchMessage"),
        "LogoControl.Title": t("map.logo"),
        "AttributionControl.ToggleAttribution": "Toggle attribution",
        "AttributionControl.MapFeedback": "Map feedback",
        "GeolocateControl.FindMyLocation": "Find my location",
        "GeolocateControl.LocationNotAvailable": "Location not available",
      };

      setMapTexts(texts);
    }
  }, [locale]);

  // --------------------------------------------------------------------------------------------------
  // SET MAP DATA - USE EFFECT - if data from optimizely is updated or the mapId is  changed
  // --------------------------------------------------------------------------------------------------

  useEffect(() => {
    if (currentCmsMapData && mapRef.current && mapContainerRef.current) {
      dispatch({
        type: MapActionsTypes.SET_MAP,
        payload: null,
      });
      if (map) {
        map.remove();
      }

      mapboxgl.accessToken = currentCmsMapData.accessToken;

      const mapboxMap = new mapboxgl.Map({
        container: mapRef.current,
        style: currentCmsMapData?.mapStyle,
        renderWorldCopies:
          currentCmsMapData.mapType === MapTypes.LINER_SERVICE_MAP
            ? true
            : false,
        minZoom:
          currentCmsMapData.mapType === MapTypes.LINER_SERVICE_MAP
            ? 3
            : undefined,
        projection:
          currentCmsMapData.mapType === MapTypes.LINER_SERVICE_MAP
            ? { name: "mercator" }
            : undefined,
        locale: mapTexts,
      });

      mapboxMap.on("load", () => {
        dispatch({
          type: MapActionsTypes.SET_MAP,
          payload: mapboxMap,
        });
      });

      mapboxMap.on("zoom", () => {});

      dispatch({
        type: MapActionsTypes.CURRENT_MAP_ID,
        payload: currentCmsMapData.mapId,
      });
    }
  }, [currentCmsMapData]);

  // --------------------------------------------------------
  // SHOW - USE EFFECT - triggers when the map should show and the map is loaded
  // --------------------------------------------------------

  useEffect(() => {
    if (show && map) {
      dispatch({
        type: MapActionsTypes.SHOW_MAP,
        payload: true,
      });
      dispatch({
        type: MapActionsTypes.SHOW_LOADER,
        payload: false,
      });
      mapSettings();
      map.resize();
    }

    if (show && !map) {
      dispatch({
        type: MapActionsTypes.SHOW_LOADER,
        payload: true,
      });
    }
  }, [show, map]);

  // --------------------------------------------------------
  // FILTER MODAL SIZE - USE EFFECT - triggers when filterModalSize is changed
  // --------------------------------------------------------

  useEffect(() => {
    if (filterModalSize && filterModalSize.height) {
      dispatch({
        type: MapActionsTypes.SET_POPUP_POSITION,
        payload: { top: filterModalSize.height + 30 },
      });
    }
  }, [filterModalSize]);

  useEffect(() => {
    if (isMobile && showMapMenu) {
      dispatch({
        type: MapActionsTypes.HIDE_FILTER,
        payload: true,
      });
    }
  }, [showMapMenu]);

  // --------------------------------------------------------
  // MAP SETTINGS - implements settings for the map
  // --------------------------------------------------------

  const mapSettings = () => {
    if (map) {
      map.resize();
      map.scrollZoom.disable();

      if (
        /Android|webOS|iPhone|iPad|iPod|BlackBerry/i.test(navigator.userAgent)
      ) {
        map.dragPan.disable();
        map.touchPitch.disable();
        map.on("touchstart", function (e: MapTouchEvent) {
          var oe = e.originalEvent;
          if (oe && "touches" in oe) {
            if (oe.touches.length > 1) {
              oe.stopImmediatePropagation();
              map?.dragPan.enable();
            } else {
              map?.dragPan.disable();
            }
          }
        });
      }
    }
  };

  // --------------------------------------------------------
  // JSX
  // --------------------------------------------------------

  return (
    <>
      {currentCmsMapData ? (
        <div
          ref={mapContainerRef}
          id="map-container"
          className={`${showMap ? "show" : ""}`}
        >
          <MapFilter
            map={map}
            show={!hideFilter && showMap}
            layers={currentCmsMapData.mapLayers}
            customMapLayers={customMapLayers}
            currentCmsMapData={currentCmsMapData}
            searchData={searchData || []}
            setFilterModalSize={(e: MapFilterSize) => {
              map?.resize();
              dispatch({
                type: MapActionsTypes.SET_FILTER_MODAL_SIZE,
                payload: e,
              });
            }}
            filterModalOpen={() => {
              dispatch({
                type: MapActionsTypes.HIDE_FILTER,
                payload: false,
              });
              if (isMobile) {
                setMapMenuHidden(true);
              }
            }}
          />
          <MapPopup
            map={map}
            selectedLocationData={selectedLocationData}
            mapConfig={mapConfig}
            position={popupPositon}
            currentCmsMapData={currentCmsMapData}
            onClose={() => setPopupClosed(true)}
            onOpen={() => setPopupClosed(false)}
          />
          <MapMenu
            show={showMapMenu}
            data={cmsMapData}
            onChange={(mapId: string) => {
              dispatch({
                type: MapActionsTypes.CURRENT_MAP_ID,
                payload: mapId,
              });

              setMapMenuHidden(true);
            }}
          />
          { hoveredItemName && popupClosed && (
            <ItemNameTooltip name={hoveredItemName} />
          )}
          <div ref={mapRef} id="map"></div>
        </div>
      ) : null}

      <div className={`map-loading ${showLoader ? "show" : "hide"}`}>
        <div className="map-loading-image"></div>
      </div>
    </>
  );
};

export default HeroMap;
